import { assign, assertEvent, sendParent, setup } from 'xstate';
import type { ClientDomainEvent } from '@/features/client/state/events';
import type { RulePackageDomainEvent } from '@/features/rule-package/state/events';
import { enterFilingFactsActor, openFilingActor, requestCalculationActor } from './filing.actors';
import type { FilingEvent } from './events';

type FilingContext = {
  filingId: string | null;
  clientId: string | null;
  rulePackageId: string | null;
  fileRef: string | null;
  error: string | null;
};

const unavailable = 'This TrustFile integration is not available in this workspace.';

export const filingMachine = setup({
  types: {
    context: {} as FilingContext,
    events: {} as FilingEvent | ClientDomainEvent | RulePackageDomainEvent,
  },
  actors: { openFilingActor, enterFilingFactsActor, requestCalculationActor },
}).createMachine({
  id: 'filing',
  type: 'parallel',
  context: { filingId: null, clientId: null, rulePackageId: null, fileRef: null, error: null },
  states: {
    workflow: {
      initial: 'idle',
      on: {
        'filing.open-dialog': { actions: sendParent({ type: 'ui.overlay.open', name: 'openFilingDialog' }) },
        'filing.hand-in': { actions: assign({ error: unavailable }) },
        'filing.approve': { actions: assign({ error: unavailable }) },
        'filing.submit': { actions: assign({ error: unavailable }) },
      },
      states: {
        idle: {
          on: {
            'filing.open': 'opening',
            'filing.enter': 'entering',
            'filing.request': 'requesting',
            'filing.open-dialog': { actions: sendParent({ type: 'ui.overlay.open', name: 'openFilingDialog' }) },
            'filing.hand-in': { actions: assign({ error: unavailable }) },
            'filing.approve': { actions: assign({ error: unavailable }) },
            'filing.submit': { actions: assign({ error: unavailable }) },
          },
        },
        opening: {
          invoke: {
            id: 'openFiling',
            src: 'openFilingActor',
            input: ({ event }) => {
              assertEvent(event, 'filing.open');
              return { clientId: event.input.clientId, rulePackageId: event.input.rulePackageId };
            },
            onDone: {
              target: 'idle',
              actions: [
                assign(({ event }) => ({ ...event.output, error: null, fileRef: null })),
                sendParent({ type: 'filing.opened' }),
                sendParent({ type: 'ui.toast.push', toast: { title: 'Filing opened', tone: 'success' } }),
                sendParent({ type: 'ui.overlay.close', name: 'openFilingDialog' }),
              ],
            },
            onError: {
              target: 'idle',
              actions: [
                assign(({ event }) => ({
                  error:
                    event.error instanceof Error
                      ? event.error.message
                      : 'The Filing service could not complete that request.',
                })),
                sendParent({ type: 'ui.toast.push', toast: { title: 'Filing could not be opened', tone: 'error' } }),
              ],
            },
          },
        },
        entering: {
          invoke: {
            id: 'enterFilingFacts',
            src: 'enterFilingFactsActor',
            input: ({ event }) => {
              assertEvent(event, 'filing.enter');
              return {
                distributions: event.input.distributions,
                fiduciaryFees: event.input.fiduciaryFees,
                filingId: event.input.filingId,
                mustDistributeIncome: event.input.mustDistributeIncome,
              };
            },
            onDone: {
              target: 'idle',
              actions: [
                assign({ error: null }),
                sendParent({ type: 'filing.facts-entered' }),
                sendParent({ type: 'ui.toast.push', toast: { title: 'Filing facts recorded', tone: 'success' } }),
              ],
            },
            onError: {
              target: 'idle',
              actions: [
                assign(({ event }) => ({
                  error:
                    event.error instanceof Error
                      ? event.error.message
                      : 'The Filing service could not complete that request.',
                })),
                sendParent({
                  type: 'ui.toast.push',
                  toast: { title: 'Filing facts could not be recorded', tone: 'error' },
                }),
              ],
            },
          },
        },
        requesting: {
          invoke: {
            id: 'requestCalculation',
            src: 'requestCalculationActor',
            input: ({ event }) => {
              assertEvent(event, 'filing.request');
              return { filingId: event.input.filingId };
            },
            onDone: {
              target: 'idle',
              actions: [
                assign({ error: null }),
                sendParent({ type: 'filing.calculation-requested' }),
                sendParent({ type: 'ui.toast.push', toast: { title: 'Calculation requested', tone: 'success' } }),
              ],
            },
            onError: {
              target: 'idle',
              actions: [
                assign(({ event }) => ({
                  error:
                    event.error instanceof Error
                      ? event.error.message
                      : 'The Filing service could not complete that request.',
                })),
                sendParent({
                  type: 'ui.toast.push',
                  toast: { title: 'Calculation could not be requested', tone: 'error' },
                }),
              ],
            },
          },
        },
      },
    },
    signals: {
      initial: 'idle',
      on: {
        'client.opened': '.idle',
        'rule-package.published': '.idle',
      },
      states: { idle: {} },
    },
  },
});
